import { useEffect, useRef, useState } from "react";

import { signInStaffWithGoogle } from "../api/console-api";
import { useApi } from "../api/context";
import { t } from "../i18n";
import { ErrorNotice } from "../shell/ErrorNotice";

/**
 * Sign in with a Google Workspace account (WALLIYO-0120).
 *
 * Google Identity Services renders its own button and hands back an ID token.
 * Nothing here decides whether that token is good: the server checks its
 * signature, that it was issued to the console's own OAuth client, that the
 * account is managed by the company's Workspace domain, and that it belongs to
 * an active staff member. This file only carries the token across.
 *
 * The script is loaded on demand rather than in index.html, so a console opened
 * without Google configured — or with Google unreachable — still shows the
 * password form and says what is wrong instead of hanging.
 */

const SCRIPT_URL = "https://accounts.google.com/gsi/client";
const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;

type GoogleCredentialResponse = { credential?: string };

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: { client_id: string; callback: (r: GoogleCredentialResponse) => void }) => void;
          renderButton: (parent: HTMLElement, options: Record<string, unknown>) => void;
        };
      };
    };
  }
}

/**
 * Loads Google Identity Services once, and resolves only when it has actually
 * loaded.
 *
 * A tag in the DOM used to be treated as "already loaded" and resolved
 * straight away. It means the download has *started*. React StrictMode mounts
 * every effect twice in development, so the second mount found the first
 * mount's tag, resolved before the script had run, read `window.google` as
 * undefined and returned without rendering anything — no button, no error,
 * nothing in the console. A race, so it looked intermittent.
 */
function loadScript(): Promise<void> {
  const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_URL}"]`);
  if (existing) {
    if (window.google) {
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("google_script_unavailable")), { once: true });
    });
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("google_script_unavailable"));
    document.head.appendChild(script);
  });
}

export function GoogleSignInButton() {
  const { client, session } = useApi();
  const target = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    if (!CLIENT_ID) {
      setUnavailable(true);
      return;
    }
    let cancelled = false;
    loadScript()
      .then(() => {
        if (cancelled || !target.current) {
          return;
        }
        if (!window.google) {
          // Loaded but the global never appeared: blocked by an extension, or
          // a network that answers for accounts.google.com. Say so rather than
          // rendering an empty space.
          setUnavailable(true);
          return;
        }
        window.google.accounts.id.initialize({
          client_id: CLIENT_ID,
          callback: (response) => {
            if (!response.credential) {
              setError(new Error("google_no_credential"));
              return;
            }
            setError(null);
            // Storing the token is what switches the console to the shell,
            // the same as verifying a code does.
            signInStaffWithGoogle(client, { idToken: response.credential })
              .then((verified) => session.set(verified.sessionToken))
              .catch(setError);
          },
        });
        window.google.accounts.id.renderButton(target.current, {
          type: "standard",
          theme: "outline",
          size: "large",
          text: "signin_with",
          width: 280,
        });
      })
      .catch(() => {
        if (!cancelled) {
          setUnavailable(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [client, session]);

  // The "or" belongs to this block, not to the page: it separates Google from
  // the password form below. Rendered by the caller it appeared even when
  // there was no button above it — a divider between a sentence and the only
  // way in — which is what a console without VITE_GOOGLE_CLIENT_ID shows, and
  // what every console has shown so far, because nothing has ever set it.
  if (unavailable) {
    return (
      <p className="meta" role="status">
        {t("signIn.googleUnavailable")}
      </p>
    );
  }

  return (
    <>
      {/* The divider sits above the button: the sign-in page puts the
          password form first and this block under it (WALLIYO-0137). */}
      <p className="meta or">{t("signIn.or")}</p>
      <div className="google-signin">
        <div ref={target} />
        {error ? <ErrorNotice error={error} context="signIn" /> : null}
      </div>
    </>
  );
}
